import DividerWithText from "../../../../components/DividerWithText";
import {
    Box,
    InputAdornment,
} from "@mui/material";
import React from "react";
import DivContainerBody from "../../../../components/DivContainerBody";
import { CustomCheckbox } from "../../../../components/CustomCheckbox";
import { CustomTextField } from "../../../../components/CustomTextField";
import {DEVICE_MODEL_NAME} from "../Model/Model";

/**
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @jobTitle: Full Stack Developer
 *
 * Device Locks Section.
 * This component renders device lock options (relays and associated timers) with conditions
 * to enable or disable the controls based on the device type and other flags.
 *
 * Constants:
 * - disableRelay0Types: Device types for which Relay0 options are disabled.
 * - enableRelay1Type: Device type that enables Relay1 options.
 * - disableTimeForRelayTypes: Device types for which the "timeForRelay" fields are disabled.
 *
 * @param {object} data - Object containing device data, including deviceType and relay states.
 * @param {function} handleChange - Function to handle input changes.
 * @param {function} t - Function for text translation.
 * @param {boolean} emptyFields - Flag to indicate if empty fields should be highlighted.
 * @param {array} deviceTypeAuthentications - (Not used in this component but kept for consistency.)
 * @returns {JSX.Element} Rendered component.
 */
const SectionCreateDeviceLocks = ({data, handleChange, t, emptyFields}) => {
    // Device types for which Relay0 options are disabled.
    // Note: Device type 13 appears twice in the original condition; it is only needed once.
    const disableRelay0Types = [
        DEVICE_MODEL_NAME.PDA_CARIBE_PL50L,
        DEVICE_MODEL_NAME.TELPO_TPS_980,
        DEVICE_MODEL_NAME.TELPO_TPS_450,
        DEVICE_MODEL_NAME.TELPO_K5,
        DEVICE_MODEL_NAME.TELPO_K10,
        DEVICE_MODEL_NAME.VERIDAS_1FA,
        DEVICE_MODEL_NAME.VERIDAS_2FA,
        DEVICE_MODEL_NAME.CAMERA_LPR,
        DEVICE_MODEL_NAME.ANTENNA_RFID,
        DEVICE_MODEL_NAME.ARATEK_BM7550,
        DEVICE_MODEL_NAME.ARATEK_BM5510,
        DEVICE_MODEL_NAME.ARATEK_BD8500,
    ];

    const enableRelay0Type = [
        DEVICE_MODEL_NAME.MANTRA_MFSTAB_II,
        DEVICE_MODEL_NAME.TELPO_F6,
        DEVICE_MODEL_NAME.TELPO_F10,
        DEVICE_MODEL_NAME.TELPO_F6_T,
        DEVICE_MODEL_NAME.ARATEK_BA8300];

    return (
        <>
            <DivContainerBody>
                <div className={"row"}>
                    <div className={"col-12"}>
                        <DividerWithText title={t("locks")} spacing={"mb-4"} />
                    </div>
                    <Box className="col-sm-12 col-md-6 col-lg-6 col-xl-6 mb-0">
                        <CustomCheckbox
                            marginbutton={true}
                            id="cb_cud_enable_relay_0"
                            checked={data.enableRelay0}
                            value={data.enableRelay0}
                            onChange={(e) => handleChange(e)}
                            labelId="lbl_cud_enableRelay0"
                            label="enableRelay0"
                            trans="enableRelay0"
                            name="enableRelay0"
                            disabled={disableRelay0Types.includes(data.deviceType)}
                        />
                    </Box>
                    <Box className="col-sm-12 col-md-6 col-lg-6 col-xl-6 mb-0">
                        <CustomCheckbox
                            marginbutton={true}
                            id="cb_cud_enable_relay_1"
                            checked={data.enableRelay1}
                            value={data.enableRelay1}
                            onChange={(e) => handleChange(e)}
                            labelId="lbl_cud_enableRelay1"
                            label="enableRelay1"
                            trans="enableRelay1"
                            name="enableRelay1"
                            disabled={true}
                        />
                    </Box>
                    <Box className="col-sm-12 col-md-6 col-lg-6 col-xl-6 mt-3">
                        <CustomTextField
                            id="txt_cud_keep_open"
                            name="timeForRelay0"
                            value={data?.timeForRelay0 || 0}
                            required={true}
                            emptyFields={emptyFields && enableRelay0Type.includes(data.deviceType)}
                            trans="keep_open_R0"
                            onChange={(e) => {
                                const value = e.target.value;
                                const regex = /^[0-9]*$/;
                                if (regex.test(value) && (value === "" || parseInt(value, 10) >= 0)) {
                                    handleChange(e);
                                }
                            }}
                            length={4}
                            disabled={!data.enableRelay0}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment className="endAdornmentStyle" position="end">
                                        {t("sec")}
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                    <Box className="col-sm-12 col-md-6 col-lg-6 col-xl-6 mt-3">
                        <CustomTextField
                            id="txt_cud_keep_open_1"
                            name="timeForRelay1"
                            value={data?.timeForRelay1}
                            trans="keep_open_R1"
                            onChange={(e) => {
                                const value = e.target.value;
                                const regex = /^[0-9]*$/;
                                if (regex.test(value) && (value === "" || parseInt(value, 10) >= 0)) {
                                    handleChange(e);
                                }
                            }}
                            length={4}
                            disabled={true}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment className="endAdornmentStyle" position="end">
                                        {t("sec")}
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                    <Box className="col-sm-12 col-md-12 col-lg-12 col-xl-12">
                        <CustomTextField
                            id="txt_cud_delay_to_open"
                            name="delayToOpen"
                            emptyFields={emptyFields && enableRelay0Type.includes(data.deviceType)}
                            required={true}
                            value={data?.delayToOpen || 0}
                            trans="delay_to_open"
                            onChange={(e) => {
                                const value = e.target.value;
                                const regex = /^[0-9]*$/;
                                if (regex.test(value) && (value === "" || parseInt(value, 10) >= 0)) {
                                    handleChange(e);
                                }
                            }}
                            length={4}
                            disabled={!data.enableRelay0}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment className='endAdornmentStyle' position='end'>
                                        {t('sec')}
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                </div>
            </DivContainerBody>
        </>
    );
};

export default SectionCreateDeviceLocks;
